import React from 'react';
import { Check, Minus } from 'lucide-react';

/**
 * Checkbox Component
 * 
 * An accessible checkbox component with label support and indeterminate state.
 * Follows BankHub design system.
 * 
 * @example
 * ```tsx
 * <Checkbox label="Accept terms" />
 * <Checkbox label="Select all" indeterminate />
 * <Checkbox 
 *   label="Subscribe" 
 *   helperText="Receive weekly updates"
 * />
 * ```
 */

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Label text */
  label?: string;
  /** Helper text displayed below checkbox */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Indeterminate state (for "select all" scenarios) */
  indeterminate?: boolean;
  /** Container className */
  containerClassName?: string;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      helperText,
      error,
      size = 'md',
      indeterminate = false,
      containerClassName = '',
      className = '',
      id,
      disabled,
      checked,
      ...props
    },
    ref
  ) => {
    // Generate ID if not provided
    const checkboxId = id || `checkbox-${React.useId()}`;

    // Internal ref for indeterminate state
    const internalRef = React.useRef<HTMLInputElement>(null);
    const checkboxRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    // Set indeterminate property (can't be set via HTML attribute)
    React.useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate, checkboxRef]);

    // Size styles for checkbox
    const sizeStyles = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    // Size styles for custom checkbox visual
    const customSizeStyles = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    // Icon size
    const iconSize = {
      sm: 12,
      md: 16,
      lg: 20,
    };

    // Label text size
    const labelSize = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    };

    return (
      <div className={`${containerClassName}`}>
        <div className="flex items-start">
          {/* Custom Checkbox Container */}
          <div className="flex items-center h-5">
            <div className="relative">
              {/* Hidden Native Checkbox */}
              <input
                ref={checkboxRef}
                type="checkbox"
                id={checkboxId}
                disabled={disabled}
                checked={checked}
                className="sr-only peer"
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={
                  error
                    ? `${checkboxId}-error`
                    : helperText
                    ? `${checkboxId}-helper`
                    : undefined
                }
                {...props}
              />

              {/* Custom Checkbox Visual */}
              <div
                className={`
                  ${customSizeStyles[size]}
                  border-2 rounded
                  transition-all duration-200
                  flex items-center justify-center
                  cursor-pointer
                  ${
                    error
                      ? 'border-error-500'
                      : 'border-gray-300 dark:border-dark-border'
                  }
                  peer-checked:bg-primary-500 peer-checked:border-primary-500
                  peer-indeterminate:bg-primary-500 peer-indeterminate:border-primary-500
                  peer-focus:ring-4 peer-focus:ring-primary-500/20
                  peer-disabled:opacity-50 peer-disabled:cursor-not-allowed
                  peer-hover:border-primary-400
                  dark:peer-checked:bg-primary-600 dark:peer-checked:border-primary-600
                  ${className}
                `}
              >
                {/* Check Icon */}
                {checked && !indeterminate && (
                  <Check className="text-white" size={iconSize[size]} strokeWidth={3} />
                )}

                {/* Indeterminate Icon */}
                {indeterminate && (
                  <Minus className="text-white" size={iconSize[size]} strokeWidth={3} />
                )}
              </div>
            </div>
          </div>

          {/* Label and Helper Text */}
          {(label || helperText) && (
            <div className="ml-3 flex-1">
              {label && (
                <label
                  htmlFor={checkboxId}
                  className={`
                    font-medium text-gray-900 cursor-pointer
                    dark:text-dark-text
                    ${labelSize[size]}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {label}
                </label>
              )}

              {helperText && !error && (
                <p
                  id={`${checkboxId}-helper`}
                  className="text-sm text-gray-500 mt-0.5 dark:text-gray-400"
                >
                  {helperText}
                </p>
              )}

              {error && (
                <p
                  id={`${checkboxId}-error`}
                  className="text-sm text-error-600 mt-0.5 dark:text-error-400"
                >
                  {error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
